import {useMemo, type FC} from 'react'
import {Flash} from '@primer/react'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {OrgFilterProvider} from '@github-ui/filter/providers'
import type {Organization, Enterprise} from '@github-ui/repos-types'
import type {Repository} from '@github-ui/current-repository'
import {BranchFilterProvider} from './providers/branch'
import {EvaluateStatusFilterProvider} from './providers/evaluate-status'
import {RulesetFilterProvider} from './providers/ruleset'
import {UserFilterProvider} from './providers/user'
import {TimePeriodFilterProvider} from './providers/time-period'
import {RuleStatusFilterProvider} from './providers/rule-status'
import {ShaFilterProvider} from './providers/sha'
import {SecurityProductsRepositoryFilterProvider} from './providers/security-products-repository-filter-provider'
import {
  SecurityProductsFilterBar,
  parseAsUser,
  getLogin,
  getName,
  timePeriodFieldConfig,
  repositoryFieldConfig,
  organizationFieldConfig,
  singleValueFilterTypes,
  type FieldConfig,
  type FilterValue,
} from './SecurityProductsFilterBar'
import type {InsightsFilter, Ruleset, SourceType} from '../../types/rules-types'
import {insightsIndexPath} from '../../helpers/insights-filter'
import {useIsStafftools} from '../../hooks/use-is-stafftools'

const INITIAL_TIME_PERIOD = 'day'
const INITIAL_EVALUATE_STATUS = 'active'
const FALLBACK_TIME_PERIOD = 'month'
const FALLBACK_EVALUATE_STATUS = 'all'

type InsightsFilterBar2Props = {
  filter: InsightsFilter
  source: Repository | Organization | Enterprise
  sourceType: SourceType
  businessSlug?: string
  rulesets: Ruleset[]
}

export const InsightsFilterBar2: FC<InsightsFilterBar2Props> = ({
  filter,
  source,
  sourceType,
  businessSlug,
  rulesets,
}) => {
  const reposRuleSuitesListBySha = useFeatureFlag('repos_rule_suites_list_by_sha')
  const isStafftools = useIsStafftools()
  const isRepoNonStafftools = sourceType === 'repository' && !isStafftools

  const filterProviders = useMemo(
    () => [
      ...(isRepoNonStafftools && reposRuleSuitesListBySha ? [new ShaFilterProvider()] : []),
      ...(isRepoNonStafftools
        ? [new BranchFilterProvider((source as Repository).ownerLogin, (source as Repository).name)]
        : []),
      ...(sourceType === 'organization' ? [new SecurityProductsRepositoryFilterProvider(source.name)] : []),
      ...(sourceType === 'enterprise' && businessSlug
        ? [new OrgFilterProvider({businessSlug, filterTypes: singleValueFilterTypes})]
        : []),
      new EvaluateStatusFilterProvider(),
      new RulesetFilterProvider(rulesets),
      new UserFilterProvider(),
      new TimePeriodFilterProvider(),
      new RuleStatusFilterProvider(),
    ],
    [rulesets, sourceType, reposRuleSuitesListBySha, businessSlug, source, isRepoNonStafftools],
  )

  const fieldConfigs = useMemo(
    () => [
      {
        filterKey: 'actor',
        queryKey: 'user',
        parse: parseAsUser,
        serialize: (v: unknown) => getLogin(v as {login: string}),
      } as FieldConfig<InsightsFilter>,
      timePeriodFieldConfig,
      {
        filterKey: 'ruleset',
        queryKey: 'ruleset',
        parse: (values: FilterValue[]) => rulesets.find(r => r.name === values[0]?.displayName),
        serialize: (v: unknown) => getName(v as {name: string}),
      } as FieldConfig<InsightsFilter>,
      {filterKey: 'ruleStatus', queryKey: 'rule-status'} as FieldConfig<InsightsFilter>,
      {filterKey: 'evaluateStatus', queryKey: 'evaluate-status'} as FieldConfig<InsightsFilter>,
      ...(reposRuleSuitesListBySha && isRepoNonStafftools
        ? [{filterKey: 'afterOid', queryKey: 'sha'} as FieldConfig<InsightsFilter>]
        : []),
      ...(isRepoNonStafftools ? [{filterKey: 'branch', queryKey: 'branch'} as FieldConfig<InsightsFilter>] : []),
      ...(sourceType === 'organization' ? [repositoryFieldConfig] : []),
      ...(sourceType === 'enterprise' ? [organizationFieldConfig] : []),
    ],
    [rulesets, reposRuleSuitesListBySha, sourceType, isRepoNonStafftools],
  )

  const filterWithDefaults = useMemo(
    () => ({
      ...filter,
      evaluateStatus: filter.evaluateStatus ?? INITIAL_EVALUATE_STATUS,
      timePeriod: filter.timePeriod ?? INITIAL_TIME_PERIOD,
    }),
    [filter],
  )

  return (
    <ErrorBoundary
      fallback={<Flash variant="warning">An error occurred loading the filter. Please refresh the page.</Flash>}
    >
      <SecurityProductsFilterBar<InsightsFilter>
        id="rules-insights-filter"
        label="Filter rules insights"
        filter={filterWithDefaults}
        fieldConfigs={fieldConfigs}
        filterProviders={filterProviders}
        getIndexPath={insightsIndexPath}
        requiredFields={{timePeriod: FALLBACK_TIME_PERIOD, evaluateStatus: FALLBACK_EVALUATE_STATUS}}
      />
    </ErrorBoundary>
  )
}
